import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../Redux/Hooks";
import {
  getWhatsAppCampaigns,
  getWhatsAppCampaignDetail,
  createWhatsAppCampaign,
  launchWhatsAppCampaign,
  getStudioAlbums,
  getRedeemUsers,
  getCampaignAudience,
  getCampaignWalletEstimate,
  getWalletBalance,
} from "../Redux/ApiCalls/Dashboard/WhatsAppAPI";
import {
  clearActiveCampaignDetail,
  clearWalletEstimate,
  clearCampaignAudience,
} from "../Redux/Slice/Dashboard/WhatsAppSlice";
import type { CreateWhatsAppCampaignPayload } from "../Data/whatsapp.dto";

/**
 * Hook for WhatsApp campaigns: list, create, launch, detail, albums, audience preview, wallet estimation.
 */
export function useCampaigns() {
  const dispatch = useAppDispatch();
  const {
    campaigns,
    loading,
    error,
    isError,
    activeCampaignDetail,
    activeCampaignLoading,
    launchingCampaignId,
    studioAlbums,
    studioAlbumsLoading,
    redeemUsers,
    redeemUsersLoading,
    campaignAudience,
    campaignAudienceLoading,
    walletEstimate,
    walletEstimateLoading,
    walletBalance,
  } = useAppSelector((state) => state.whatsapp);

  const refresh = useCallback(
    (status?: string) => {
      dispatch(getWhatsAppCampaigns(status));
      dispatch(getWalletBalance());
    },
    [dispatch]
  );

  const createCampaign = useCallback(
    (payload: CreateWhatsAppCampaignPayload) => {
      return dispatch(createWhatsAppCampaign(payload));
    },
    [dispatch]
  );

  const launchCampaign = useCallback(
    (campaignId: string) => {
      return dispatch(launchWhatsAppCampaign(campaignId));
    },
    [dispatch]
  );

  const getCampaignDetail = useCallback(
    (campaignId: string) => {
      return dispatch(getWhatsAppCampaignDetail(campaignId));
    },
    [dispatch]
  );

  const fetchStudioAlbums = useCallback(() => {
    return dispatch(getStudioAlbums());
  }, [dispatch]);

  const fetchRedeemUsers = useCallback(
    (params?: { album_id?: number | string; album_ids?: Array<number | string> | string }) => {
      return dispatch(getRedeemUsers(params));
    },
    [dispatch]
  );

  const fetchCampaignAudience = useCallback(
    (params?: { album_id?: number | string; album_ids?: Array<number | string> | string }) => {
      return dispatch(getCampaignAudience(params));
    },
    [dispatch]
  );

  const fetchWalletEstimate = useCallback(
    (params: {
      template_id: string;
      album_id?: number | string;
      album_ids?: Array<number | string> | string;
    }) => {
      return dispatch(getCampaignWalletEstimate(params));
    },
    [dispatch]
  );

  const clearActiveCampaign = useCallback(() => {
    dispatch(clearActiveCampaignDetail());
  }, [dispatch]);

  const clearEstimate = useCallback(() => {
    dispatch(clearWalletEstimate());
  }, [dispatch]);

  const clearAudience = useCallback(() => {
    dispatch(clearCampaignAudience());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getWhatsAppCampaigns(undefined));
    dispatch(getWalletBalance());
  }, [dispatch]);

  return {
    campaigns,
    loading,
    error,
    isError,
    activeCampaignDetail,
    activeCampaignLoading,
    launchingCampaignId,
    studioAlbums,
    studioAlbumsLoading,
    redeemUsers,
    redeemUsersLoading,
    campaignAudience,
    campaignAudienceLoading,
    walletEstimate,
    walletEstimateLoading,
    walletBalance,
    refresh,
    createCampaign,
    launchCampaign,
    getCampaignDetail,
    fetchStudioAlbums,
    fetchRedeemUsers,
    fetchCampaignAudience,
    fetchWalletEstimate,
    clearActiveCampaign,
    clearEstimate,
    clearAudience,
  };
}
